import { v4 as uuid } from 'uuid';

/**
 * Cálculos do Glossário de siglas, sem React: limpar e ordenar as siglas,
 * agrupar pela letra, filtrar pela busca e ler a lista colada no editor.
 */

const semAcento = (texto) =>
  (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const chaveDeOrdem = (sigla) => semAcento(sigla).toUpperCase();

/** Siglas com sigla e significado, em ordem alfabética (sem olhar acento). */
export const itensDe = (data) =>
  (data?.itens || [])
    .map((x, i) => ({
      id: x?.['@id'] || `s${i}`,
      sigla: (x?.sigla || '').trim(),
      significado: (x?.significado || '').trim(),
      vinculo: (x?.vinculo || '').trim(),
    }))
    .filter((x) => x.sigla && x.significado)
    .sort((a, b) => {
      const ka = chaveDeOrdem(a.sigla);
      const kb = chaveDeOrdem(b.sigla);
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });

/** A letra do índice; o que não começa por letra fica em "#". */
export const letraDe = (sigla) => {
  const l = chaveDeOrdem(sigla).charAt(0);
  return /[A-Z]/.test(l) ? l : '#';
};

/** [{ letra, itens }] na ordem das siglas. */
export const agrupar = (itens) =>
  itens.reduce((grupos, x) => {
    const letra = letraDe(x.sigla);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.letra === letra) ultimo.itens.push(x);
    else grupos.push({ letra, itens: [x] });
    return grupos;
  }, []);

/** Busca na sigla, no significado e no vínculo, sem distinguir acento nem caixa. */
export const filtrar = (itens, busca) => {
  const q = semAcento(busca).toLowerCase().trim();
  if (!q) return itens;
  return itens.filter((x) =>
    [x.sigla, x.significado, x.vinculo].some((campo) =>
      semAcento(campo).toLowerCase().includes(q),
    ),
  );
};

// Sigla: até 20 caracteres sem espaço, com pelo menos duas maiúsculas
// (DSEI, AgSUS, GAB-DIOP, PrEP).
const ehSigla = (s) => (s.match(/[A-Z]/g) || []).length >= 2;
// "SIGLA – significado", com travessão, dois-pontos, tabulação ou hífen
// entre espaços; "| vínculo" no fim é opcional.
const SIGLA_PRIMEIRO = /^(\S{1,20}?)\s*(?:[–—:\t]|\s-\s)\s*(.+)$/;
// "Significado por extenso (SIGLA)".
const SIGLA_NO_FIM = /^(.+?)\s*\(\s*(\S{1,20})\s*\)$/;

/** Lê a lista colada (uma sigla por linha; de planilha, as colunas sigla,
 *  significado e vínculo) e devolve as siglas novas. */
export const lerListaColada = (texto) => {
  const itens = [];
  const ignoradas = [];
  (texto || '')
    .normalize('NFC')
    .split('\n')
    .map((l) => l.replace(/^\s*[-•*]\s+/, '').trim())
    .filter(Boolean)
    .forEach((linha) => {
      const colunas = linha.split('\t').map((c) => c.trim());
      const [corpo, ...resto] =
        colunas.length > 1
          ? [`${colunas[0]}\t${colunas[1]}`, ...colunas.slice(2)]
          : linha.split('|');
      const vinculo = resto.join(' ').trim();
      let sigla = '';
      let significado = '';
      const comeco = corpo.trim().match(SIGLA_PRIMEIRO);
      const fim = corpo.trim().match(SIGLA_NO_FIM);
      if (comeco && ehSigla(comeco[1])) [, sigla, significado] = comeco;
      else if (fim && ehSigla(fim[2])) [, significado, sigla] = fim;
      significado = (significado || '').trim();
      // "UGP – Unidade de Gestão de Pessoas (UGP)": o parêntese repete a sigla
      if (sigla && significado.endsWith(`(${sigla})`))
        significado = significado.slice(0, -(sigla.length + 2)).trim();
      significado = significado.replace(/[.;]$/, '');
      if (!sigla || !significado) {
        ignoradas.push(linha);
        return;
      }
      itens.push({
        '@id': uuid(),
        sigla,
        significado,
        ...(vinculo ? { vinculo } : {}),
      });
    });
  return { itens, ignoradas };
};
